import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MessageSquare, Star, Send, X, Trophy, Heart, Loader2, MapPin, Filter,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import PhotoUploader from "@/components/PhotoUploader";
import { BadgeStrip } from "@/components/BadgeStrip";

/**
 * The Holler — community testimonial wall. "Holler" is Appalachian for a
 * small valley AND for "give us a holler / speak up". Hunters share their
 * experiences whether they won or didn't.
 *
 * This page is intentionally loud and proud:
 *   - Big hero saying "Give us a holler"
 *   - Filter pills (All / Winners / Just had fun / w/ Photos)
 *   - Masonry-ish card grid with rating stars + did_win trophy
 *   - "Add yours" CTA opens a modal for submission (rate-limited server-side)
 *   - All submissions land as "pending" until the admin approves them.
 */
export default function TheHollerPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [filter, setFilter] = useState("all");  // all | winners | fun | photos
  const [modalOpen, setModalOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/holler", { params: { limit: 80 } });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setEnabled(data.enabled !== false);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    if (filter === "winners") return items.filter((x) => x.did_win);
    if (filter === "fun") return items.filter((x) => !x.did_win);
    if (filter === "photos") return items.filter((x) => x.photo_url);
    return items;
  }, [items, filter]);

  if (!loading && !enabled) {
    return (
      <div className="py-20 text-center space-y-4" data-testid="holler-disabled">
        <MessageSquare size={40} className="mx-auto text-[#F5F2E8]/30" />
        <h1 className="font-display uppercase text-4xl">The Holler is quiet.</h1>
        <p className="text-sm text-[#F5F2E8]/65 max-w-md mx-auto">
          We've paused community submissions for now. Check back soon — or follow a hunt live in the meantime.
        </p>
        <Link to="/" className="btn-loot text-xs inline-block mt-2">Back to home</Link>
      </div>
    );
  }

  return (
    <div className="py-10 space-y-8" data-testid="holler-page">
      {/* Hero */}
      <header className="relative">
        <div className="max-w-3xl">
          <div className="overline">Word from the trail</div>
          <h1 className="font-display uppercase text-5xl md:text-7xl tracking-tight mt-2 leading-[0.9]">
            The
            <span style={{ color: "#76B82A" }}> Holler</span>.
          </h1>
          <p className="mt-5 text-lg text-[#F5F2E8]/85 max-w-xl">
            Real hunters. Real stories. Win or lose, hot trail or cold —
            <span className="text-[#D7A62A]"> give us a holler.</span> The wins are loud, but the
            close calls and "we got skunked but had a blast" stories make this thing what it is.
          </p>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setModalOpen(true)}
              className="btn-loot inline-flex items-center gap-2"
              data-testid="holler-add-btn"
            >
              <MessageSquare size={14} /> Add your holler
            </button>
            <span className="font-mono text-xs text-[#F5F2E8]/55">
              {total} {total === 1 ? "voice" : "voices"} · curated by hand
            </span>
          </div>
        </div>
      </header>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="holler-filters">
        <Filter size={12} className="text-[#F5F2E8]/55" />
        <FilterPill v="all"     label="Everyone"      cur={filter} set={setFilter} />
        <FilterPill v="winners" label="Lootmakers"    cur={filter} set={setFilter} accent="#76B82A" />
        <FilterPill v="fun"     label="Just for fun"  cur={filter} set={setFilter} accent="#D7A62A" />
        <FilterPill v="photos"  label="With photos"   cur={filter} set={setFilter} />
      </div>

      {/* Wall */}
      {loading ? (
        <div className="text-center py-16 font-mono text-sm text-[#F5F2E8]/50">
          <Loader2 size={24} className="inline animate-spin mr-2" /> Loading hollers…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState filter={filter} onAdd={() => setModalOpen(true)} />
      ) : (
        <div
          className="grid gap-5 md:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="holler-grid"
        >
          {filtered.map((t) => <HollerCard key={t.id} t={t} />)}
        </div>
      )}

      {modalOpen && <SubmitModal onClose={() => setModalOpen(false)} onDone={reload} />}
    </div>
  );
}

function FilterPill({ v, label, cur, set, accent }) {
  const active = cur === v;
  return (
    <button
      onClick={() => set(v)}
      data-testid={`holler-filter-${v}`}
      className="px-3 py-1.5 font-mono uppercase tracking-widest text-[10px] border transition-colors"
      style={{
        color:        active ? "#0F0F0F" : (accent || "#F5F2E8"),
        background:   active ? (accent || "#F5F2E8") : "transparent",
        borderColor:  active ? (accent || "#F5F2E8") : "rgba(245,242,232,0.25)",
      }}
    >
      {label}
    </button>
  );
}

function HollerCard({ t }) {
  return (
    <article
      className={`relative loot-card-flat p-5 flex flex-col gap-3 ${t.featured ? "ring-2 ring-[#D7A62A]" : ""}`}
      data-testid={`holler-card-${t.id}`}
    >
      {t.featured && (
        <div
          className="absolute -top-2 left-3 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest"
          style={{ background: "#D7A62A", color: "#0F0F0F" }}
        >
          ★ Featured
        </div>
      )}
      {/* Rating + win badge */}
      <div className="flex items-center justify-between gap-2">
        {t.rating ? (
          <div className="flex items-center gap-0.5" aria-label={`${t.rating} of 5`}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                size={14}
                fill={i <= t.rating ? "#D7A62A" : "transparent"}
                stroke={i <= t.rating ? "#D7A62A" : "rgba(245,242,232,0.35)"}
              />
            ))}
          </div>
        ) : <span />}
        {t.did_win ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border"
            style={{ color: "#76B82A", borderColor: "#76B82A" }}
          >
            <Trophy size={10} /> Lootmaker
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest border"
            style={{ color: "#D7A62A", borderColor: "rgba(215,166,42,0.4)" }}
          >
            <Heart size={10} /> Just had fun
          </span>
        )}
      </div>

      {t.photo_url && (
        <div className="-mx-5 -mt-1">
          <img
            src={t.photo_url}
            alt={t.title || "Holler photo"}
            className="w-full h-44 object-cover"
            loading="lazy"
          />
        </div>
      )}

      {t.title && (
        <h3 className="font-display uppercase text-xl leading-tight">{t.title}</h3>
      )}

      <p className="text-sm text-[#F5F2E8]/85 leading-relaxed whitespace-pre-line">
        “{t.body}”
      </p>

      <div className="mt-auto pt-3 border-t border-[#F5F2E8]/10 flex items-center justify-between gap-2 text-xs">
        <span className="font-display uppercase tracking-wider inline-flex items-center gap-2">
          {t.name}
          <BadgeStrip username={t.name} max={5} size={20} />
        </span>
        {t.city && (
          <span className="font-mono text-[10px] text-[#F5F2E8]/55 inline-flex items-center gap-1">
            <MapPin size={10} /> {t.city}
          </span>
        )}
      </div>
    </article>
  );
}

function EmptyState({ filter, onAdd }) {
  const labels = {
    all:     "No hollers yet — be the first.",
    winners: "No lootmaker stories here yet. Win or lose, share yours.",
    fun:     "No fun-only stories yet. Drop one!",
    photos:  "No photo hollers yet. Pics or it didn't happen.",
  };
  return (
    <div className="text-center py-20 border border-dashed border-[#F5F2E8]/15">
      <MessageSquare size={36} className="mx-auto text-[#F5F2E8]/30" />
      <p className="mt-4 text-sm text-[#F5F2E8]/70 max-w-md mx-auto">{labels[filter] || labels.all}</p>
      <button onClick={onAdd} className="btn-loot text-xs mt-4 inline-flex items-center gap-1">
        <Send size={12} /> Holler at us
      </button>
    </div>
  );
}

function SubmitModal({ onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", city: "", title: "", body: "",
    rating: 5, photo_url: "", did_win: false,
  });

  const submit = async () => {
    if (form.body.trim().length < 10) {
      toast.error("Tell us at least a sentence (10+ chars).");
      return;
    }
    if (!form.name.trim()) {
      toast.error("A name or handle, please.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/holler", {
        name: form.name.trim(),
        city: form.city.trim() || null,
        title: form.title.trim() || null,
        body: form.body.trim(),
        rating: form.rating || null,
        photo_url: form.photo_url || null,
        did_win: !!form.did_win,
      });
      toast.success("Thanks! Your holler is in — we'll review and post soon.");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't submit");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="loot-card-flat w-full max-w-xl max-h-[92vh] overflow-y-auto p-6 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="holler-submit-modal"
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center border border-[#F5F2E8]/20 hover:border-[#E53935] hover:text-[#E53935]"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="overline">Add your holler</div>
        <h2 className="font-display uppercase text-3xl mt-1">
          Tell us how it went.
        </h2>
        <p className="text-sm text-[#F5F2E8]/70 mt-2">
          Win, lose, or hot-trail-cold-tracked — we want to hear it. Reviewed by hand before posting.
        </p>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            className="loot-input"
            placeholder="Your name or @handle"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            maxLength={80}
            data-testid="holler-form-name"
          />
          <input
            className="loot-input"
            placeholder="City (optional)"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            maxLength={80}
          />
        </div>

        <input
          className="loot-input mt-3"
          placeholder='Headline (optional, e.g. "Tracked it for 3 hours, found nothing, came back tomorrow")'
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          maxLength={120}
        />

        <textarea
          className="loot-input mt-3"
          rows={5}
          placeholder="What happened? How'd it feel? Any moments you'll never forget?"
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
          maxLength={1500}
          data-testid="holler-form-body"
        />
        <div className="text-right font-mono text-[10px] text-[#F5F2E8]/45">
          {form.body.length} / 1500
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs text-[#F5F2E8]/55">Stars:</span>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setForm({ ...form, rating: n })}
              className="p-1"
              aria-label={`${n} stars`}
            >
              <Star
                size={20}
                fill={n <= form.rating ? "#D7A62A" : "transparent"}
                stroke={n <= form.rating ? "#D7A62A" : "rgba(245,242,232,0.45)"}
              />
            </button>
          ))}
        </div>

        <label className="mt-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.did_win}
            onChange={(e) => setForm({ ...form, did_win: e.target.checked })}
          />
          <span>I found loot on this hunt 🏆</span>
        </label>

        <div className="mt-4">
          <div className="overline mb-1">Photo (optional)</div>
          <PhotoUploader
            value={form.photo_url}
            onChange={(url) => setForm({ ...form, photo_url: url })}
            testid="holler-form-photo"
          />
        </div>

        <div className="mt-5 pt-4 border-t border-[#F5F2E8]/10 flex items-center gap-2 justify-end">
          <button onClick={onClose} className="btn-ghost text-xs">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="btn-loot text-xs inline-flex items-center gap-1"
            data-testid="holler-submit-btn"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
            {busy ? "Sending…" : "Send my holler"}
          </button>
        </div>
      </div>
    </div>
  );
}
